import type { Message } from '../InlineChat';
import type { PortalOpenDocumentMeta, PortalSelectedDocumentPayload } from './portalDocuments';
import { parseReasoningTrace, type ReasoningTrace } from './reasoningTrace';
import { portalAuthzHeaders } from '../../constants';

/** Phase 2a: Queue + Recovery · Phase 2b: bis zu 2 parallel. */
export const MAX_PARALLEL_BACKGROUND_JOBS = 2;

const BACKGROUND_JOBS_STORAGE_PREFIX = 'arkorbit_portal_arko_background_jobs_v2__';
/** Phase-1-Singleton (Migration). */
const LEGACY_BACKGROUND_JOB_STORAGE_PREFIX = 'arkorbit_portal_arko_background_job_v1__';

export interface BackgroundJobSnapshot {
  documentId: string;
  documentName: string;
  request_id: string;
  startedAt: number;
  threadId: string;
  capturedHistory: Array<Pick<Message, 'role' | 'text'>>;
  capturedDocumentContext: string;
  capturedOpenDocuments?: PortalOpenDocumentMeta[];
  capturedSelectedDocuments?: PortalSelectedDocumentPayload[];
  capturedFingerprint?: string | null;
  capturedExtraBody?: Record<string, unknown>;
  mode?: string;
}

interface BackgroundJobQueueFile {
  version: 2;
  jobs: BackgroundJobSnapshot[];
}

export interface BackgroundJobCompletionNotice {
  id: string;
  threadId: string;
  documentName: string;
  success: boolean;
  summary: string;
}

export function backgroundJobsStorageKey(customerId: string): string {
  return `${BACKGROUND_JOBS_STORAGE_PREFIX}${customerId}`;
}

function legacyBackgroundJobStorageKey(customerId: string): string {
  return `${LEGACY_BACKGROUND_JOB_STORAGE_PREFIX}${customerId}`;
}

function isValidSnapshot(value: unknown): value is BackgroundJobSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Partial<BackgroundJobSnapshot>;
  return (
    typeof o.documentId === 'string'
    && typeof o.documentName === 'string'
    && typeof o.request_id === 'string'
    && typeof o.startedAt === 'number'
    && typeof o.threadId === 'string'
    && typeof o.capturedDocumentContext === 'string'
    && Array.isArray(o.capturedHistory)
  );
}

function migrateLegacySingleJob(customerId: string): BackgroundJobSnapshot[] {
  try {
    const raw = sessionStorage.getItem(legacyBackgroundJobStorageKey(customerId));
    if (!raw) return [];
    const value = JSON.parse(raw) as Partial<BackgroundJobSnapshot>;
    sessionStorage.removeItem(legacyBackgroundJobStorageKey(customerId));
    if (!isValidSnapshot(value)) return [];
    return [value];
  } catch {
    return [];
  }
}

export function loadBackgroundJobQueue(customerId: string): BackgroundJobSnapshot[] {
  try {
    const raw = sessionStorage.getItem(backgroundJobsStorageKey(customerId));
    if (!raw) return migrateLegacySingleJob(customerId);
    const parsed = JSON.parse(raw) as Partial<BackgroundJobQueueFile> | BackgroundJobSnapshot[];
    if (Array.isArray(parsed)) {
      return parsed.filter(isValidSnapshot);
    }
    if (parsed?.version === 2 && Array.isArray(parsed.jobs)) {
      return parsed.jobs.filter(isValidSnapshot);
    }
    sessionStorage.removeItem(backgroundJobsStorageKey(customerId));
    return migrateLegacySingleJob(customerId);
  } catch {
    return migrateLegacySingleJob(customerId);
  }
}

export function saveBackgroundJobQueue(customerId: string, jobs: BackgroundJobSnapshot[]): void {
  try {
    const key = backgroundJobsStorageKey(customerId);
    if (jobs.length === 0) {
      sessionStorage.removeItem(key);
      return;
    }
    const payload: BackgroundJobQueueFile = { version: 2, jobs };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* privater Modus — React-State bleibt maßgeblich */
  }
}

export function clearBackgroundJobStorage(customerId: string): void {
  try { sessionStorage.removeItem(backgroundJobsStorageKey(customerId)); } catch { /* noop */ }
  try { sessionStorage.removeItem(legacyBackgroundJobStorageKey(customerId)); } catch { /* noop */ }
}

export function newBackgroundRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function shortDocumentLabel(name: string, max = 26): string {
  const trimmed = name.trim() || 'Dokument';
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

export function backgroundReasoningTrace(
  requestId: string,
  parsed: ReasoningTrace | null,
  error: string | null = null,
): ReasoningTrace {
  if (parsed) {
    return {
      ...parsed,
      request_id: parsed.request_id ?? requestId,
      pipeline: [
        { step: 'background_document_analysis', ok: error == null },
        ...(parsed.pipeline ?? []),
      ],
      error: parsed.error ?? error,
    };
  }
  return {
    intent: 'zusammenfassen',
    path: 'background_document_analysis',
    not_triggered: [],
    request_id: requestId,
    pipeline: [{ step: 'background_document_analysis', ok: error == null }],
    warnings: [],
    error,
  };
}

export function threadHasBackgroundJobResult(
  messages: Message[] | undefined,
  requestId: string,
): boolean {
  if (!messages?.length) return false;
  return messages.some((msg) => msg.reasoningTrace?.request_id === requestId);
}

export interface RunBackgroundJobParams {
  job: BackgroundJobSnapshot;
  webhookUrl: string;
  timeoutMs: number;
  signal: AbortSignal;
  /** Snapshot at call time — prefer {@link isCancelled} for in-flight abort. */
  cancelled?: boolean;
  isCancelled?: () => boolean;
}

export type BackgroundJobRunOutcome =
  | { kind: 'success'; reply: string; trace: ReasoningTrace }
  | { kind: 'error'; trace: ReasoningTrace; timedOut: boolean; cancelled: boolean };

function backgroundAnalysisChatInput(documentName: string, selectedCount: number): string {
  if (selectedCount >= 2) {
    return `Analysiere die ausgewählten Dokumente (${documentName}) und fasse die wichtigsten Inhalte, Risiken und nächsten sinnvollen Schritte strukturiert zusammen.`;
  }
  return `Analysiere das Dokument „${documentName}“ und fasse die wichtigsten Inhalte, Risiken und nächsten sinnvollen Schritte strukturiert zusammen.`;
}

function extractWebhookReply(payload: unknown, fallback: unknown): string {
  const p = payload as Record<string, unknown> | null | undefined;
  const reply =
    p?.output ?? p?.text ?? p?.message ??
    (typeof p?.message === 'object' && p?.message !== null
      ? (p.message as { content?: unknown }).content
      : undefined) ??
    p?.content ??
    (typeof payload === 'string' ? payload : null);
  if (typeof reply === 'string' && reply.trim()) return reply;
  return typeof fallback === 'string' ? fallback : JSON.stringify(fallback);
}

export async function runBackgroundDocumentAnalysis(
  params: RunBackgroundJobParams & { cancelled?: boolean },
): Promise<BackgroundJobRunOutcome> {
  const { job, webhookUrl, signal, cancelled = false, isCancelled } = params;
  const userCancelled = () => (isCancelled?.() ?? cancelled);
  const { documentId, documentName, request_id: requestId, threadId } = job;
  const selectedDocs = job.capturedSelectedDocuments?.filter((d) => d.documentContext?.trim()) ?? [];
  const isMultiSelect = selectedDocs.length >= 1;

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId,
        ...portalAuthzHeaders(webhookUrl),
      },
      body: JSON.stringify({
        chatInput: backgroundAnalysisChatInput(documentName, isMultiSelect ? selectedDocs.length : 1),
        history: job.capturedHistory,
        mode: job.mode,
        request_id: requestId,
        intent: 'zusammenfassen',
        ...(job.capturedExtraBody ?? {}),
        threadId,
        documentContext: job.capturedDocumentContext,
        activeDocumentId: documentId,
        ...(job.capturedOpenDocuments?.length ? { openDocuments: job.capturedOpenDocuments } : {}),
        ...(isMultiSelect ? { selectedDocuments: selectedDocs } : {}),
        ...(job.capturedFingerprint ? { documentExtractFingerprint: job.capturedFingerprint } : {}),
        background: true,
        background_job: true,
        backgroundJob: {
          kind: 'document_analysis',
          documentId,
          startedAt: job.startedAt,
          ...(isMultiSelect ? { selectedDocumentIds: selectedDocs.map((d) => d.documentId) } : {}),
        },
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    const raw = data?.data ?? data;
    const item = Array.isArray(raw) ? raw[0] : raw;
    const payload = item?.json ?? item ?? data;
    const trace = backgroundReasoningTrace(
      requestId,
      parseReasoningTrace(payload?.reasoning_trace),
    );

    return {
      kind: 'success',
      reply: extractWebhookReply(payload, data),
      trace,
    };
  } catch (error) {
    const aborted = (error as { name?: string })?.name === 'AbortError';
    const cancelledNow = userCancelled();
    const timedOut = aborted && !cancelledNow;
    return {
      kind: 'error',
      timedOut,
      cancelled: cancelledNow || (aborted && !timedOut),
      trace: backgroundReasoningTrace(
        requestId,
        null,
        cancelledNow ? 'background_cancelled' : timedOut ? 'background_timeout' : 'background_request_failed',
      ),
    };
  }
}

export function buildBackgroundJobSnapshot(args: {
  documentId: string;
  documentName: string;
  threadId: string;
  history: Message[];
  documentContext: string;
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  selectedDocuments?: PortalSelectedDocumentPayload[];
  documentExtractFingerprint?: string | null;
  extraBody?: Record<string, unknown>;
  mode?: string;
}): BackgroundJobSnapshot {
  return {
    documentId: args.documentId,
    documentName: args.documentName,
    request_id: newBackgroundRequestId(),
    startedAt: Date.now(),
    threadId: args.threadId,
    capturedHistory: args.history.map((m) => ({ role: m.role, text: m.text })),
    capturedDocumentContext: args.documentContext,
    capturedOpenDocuments: args.openDocumentsMeta,
    capturedSelectedDocuments: args.selectedDocuments,
    capturedFingerprint: args.documentExtractFingerprint,
    capturedExtraBody: args.extraBody,
    mode: args.mode,
  };
}
